import {
  computed, onMounted, onUnmounted, readonly, ref, watch,
} from 'vue';
import type { AxiosInstance } from 'axios';
import Axios from 'axios';
import { useForm } from '@inertiajs/vue3';
import { debounce } from 'lodash-es';
import GlobalSearchResultsData = App.Data.Search.GlobalSearchResultsData;
import GroupedSearchResultsData = App.Data.Search.GroupedSearchResultsData;
import SearchResultData = App.Data.Search.SearchResultData;
import { index as accountsIndex } from '@/routes/accounts';
import { index as usersIndex } from '@/routes/users';
import { index as companiesIndex } from '@/routes/companies';
import { index as brandsIndex } from '@/routes/brands';
import { search as globalSearch } from '@/routes/global';

const GlobalSearchResultsDataPlaceholder: GlobalSearchResultsData = {
  results: [
    {
      name: 'Accounts',
      viewAllUrl: accountsIndex.url(),
      searchResults: [],
    },
    {
      name: 'Users',
      viewAllUrl: usersIndex.url(),
      searchResults: [],
    },
    {
      name: 'Companies',
      viewAllUrl: companiesIndex.url(),
      searchResults: [],
    },
    {
      name: 'Brands',
      viewAllUrl: brandsIndex.url(),
      searchResults: [],
    },
  ] as GroupedSearchResultsData[],
};

const query = ref('');
const activeItem = ref<string | null>(null);
const loading = ref<boolean>(false);
const isSearchPaletteOpen = ref<boolean>(false);
const searchResults = ref<GlobalSearchResultsData>(GlobalSearchResultsDataPlaceholder);
const SEARCH_KEY = '/';

interface Options {
  axios?: AxiosInstance;
}

export default function useGlobalSearch(options: Options = {}) {
  const axios = options.axios || Axios;

  const openSearchPalette = () => {
    isSearchPaletteOpen.value = true;
  };

  const setSearchResults = (results: GlobalSearchResultsData) => {
    searchResults.value = results;
  };

  const closeSearchPalette = () => {
    setSearchResults(GlobalSearchResultsDataPlaceholder);
    isSearchPaletteOpen.value = false;
  };

  const search = (queryString: string) => {
    if (queryString.length < 2) {
      return;
    }

    loading.value = true;
    axios.get(globalSearch.url(), {
      params: {
        query: queryString,
      },
    }).then((response) => {
      setSearchResults(response.data as GlobalSearchResultsData);
    }).finally(() => {
      loading.value = false;
    });
  };

  const handleKeydown = (event: KeyboardEvent): void => {
    const activeElement = document.activeElement as HTMLElement;

    // Check if active element is an input, textarea, or contentEditable element
    const isInputFocused = activeElement.tagName === 'INPUT'
        || activeElement.tagName === 'TEXTAREA'
        || activeElement.isContentEditable;

    if (event.key === SEARCH_KEY && !isInputFocused) {
      event.preventDefault();
      if (isSearchPaletteOpen.value) {
        closeSearchPalette();
      } else {
        openSearchPalette();
      }
    }
  };

  const clearSearchResults = () => {
    searchResults.value = GlobalSearchResultsDataPlaceholder;
  };

  const flatSearchResults = computed(() => searchResults.value.results
    ?.flatMap((result: GroupedSearchResultsData) => result.searchResults || [])
    .filter((item: SearchResultData | null) => item !== null) ?? []);

  const setActiveItem = (item: SearchResultData) => {
    activeItem.value = item.id;
  };

  const isActiveItem = (item: SearchResultData) => activeItem.value && activeItem.value === item.id;

  const view = (url: string) => {
    useForm({}).get(url);
    closeSearchPalette();
  };

  const shadow = (url: string) => {
    useForm({}).post(url);
    closeSearchPalette();
  };

  const handleEnterKey = (event: KeyboardEvent) => {
    if (event.key === 'Enter' && activeItem.value) {
      const index = flatSearchResults.value.findIndex((item) => item.id === activeItem.value);
      if (index >= 0) {
        view(flatSearchResults.value[index].viewUrl);
      }
    }
  };

  const handleTabKey = (event: KeyboardEvent) => {
    if (event.key === 'Tab' && activeItem.value) {
      const index = flatSearchResults.value.findIndex((item) => item.id === activeItem.value);
      if (index >= 0 && flatSearchResults.value[index].shadowUrl) {
        shadow(flatSearchResults.value[index].shadowUrl as string);
      }
    }
  };

  const activeItemNext = () => {
    const index = flatSearchResults.value.findIndex((item) => item.id === activeItem.value);
    if (index < flatSearchResults.value.length - 1) {
      activeItem.value = flatSearchResults.value[index + 1].id;
    }
  };

  const activeItemPrevious = () => {
    const index = flatSearchResults.value.findIndex((item) => item.id === activeItem.value);
    if (index > 0) {
      activeItem.value = flatSearchResults.value[index - 1].id;
    }
  };

  const debouncedSearch = debounce((queryString: string | null) => {
    if (queryString === null || queryString.length === 0) {
      clearSearchResults();
    } else {
      search(queryString);
    }
  }, 450);

  watch(query, () => {
    activeItem.value = null;
    debouncedSearch(query.value);
  });

  watch(searchResults, () => {
    if (activeItem.value === null) {
      const [firstResult] = flatSearchResults.value;
      activeItem.value = firstResult ? firstResult.id : null;
    }
  });

  onMounted(() => {
    window.addEventListener('keydown', handleKeydown);
  });

  onUnmounted(() => {
    window.removeEventListener('keydown', handleKeydown);
  });

  return {
    isSearchPaletteOpen: readonly(isSearchPaletteOpen),
    searchResults: readonly(searchResults),
    loading: readonly(loading),
    openSearchPalette,
    closeSearchPalette,
    search,
    clearSearchResults,
    query,
    flatSearchResults: readonly(flatSearchResults),
    activeItem: readonly(activeItem),
    setActiveItem,
    isActiveItem,
    view,
    shadow,
    handleEnterKey,
    handleTabKey,
    activeItemNext,
    activeItemPrevious,
    setSearchResults,
  };
}
